import {toast} from '@wandb/weave/common/components/elements/Toast';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {capitalizeFirst} from '@wandb/weave/core/util/string';
import React, {useCallback, useMemo} from 'react';
import {Slide} from 'react-toastify';
import {twMerge} from 'tailwind-merge';

import {useUpsertWorkspaceTemplateMutation} from '../../generated/graphql';
import {trackWorkspaceTemplate} from '../../services/analytics/workspaceTemplateSettings';
import {propagateErrorsContext} from '../../util/errors';
import {InstrumentedCenteredWaveLoader} from '../utility/InstrumentedLoader';
import {LinePlotSettings} from '../WorkspaceDrawer/Settings/types';
import {useWorkspaceSettingsContext} from '../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {LINE_PLOT_TEMPLATE_FIELDS, TW_DRAWER_WIDTH} from './constants';
import {useTemplateSections} from './useTemplateSections';
import {useWorkspaceTemplateDrawerContext} from './WorkspaceTemplateDrawerContextProvider';
import {
  LinePlotTemplateSettings,
  WorkspaceTemplate,
} from './workspaceTemplatesQueries';

export const WorkspaceTemplateContentFooter = ({
  setError,
}: {
  setError: (error: string | undefined) => void;
}) => {
  const {
    entityOrgId,
    isLoadingWorkspaceTemplates,
    refetchWorkspaceTemplates,
    workspaceTemplateMode,
    workspaceTemplates,
    closeWorkspaceTemplateDrawer,
  } = useWorkspaceTemplateDrawerContext();
  // For the time being, we only support 1 template per user within an org
  // This should be updated to support multiple templates in the future
  const viewerWorkspaceTemplate = workspaceTemplates[0];

  const {linePlot} = useWorkspaceSettingsContext();

  const [upsertWorkspaceTemplate, {loading: isUpsertingWorkspaceTemplate}] =
    useUpsertWorkspaceTemplateMutation();

  const linePlotSettings = useWorkspaceLinePlotValues({
    workspaceTemplate: viewerWorkspaceTemplate,
    currWorkspaceLinePlotSettings: linePlot,
  });

  const onButtonClick = useCallback(async () => {
    if (entityOrgId == null) {
      return;
    }

    setError(undefined);
    try {
      await upsertWorkspaceTemplate({
        variables: {
          entityId: entityOrgId,
          linePlotSettings,
          id: viewerWorkspaceTemplate?.id,
        },
        context: propagateErrorsContext(),
      });
      toast(
        `Personal settings template ${workspaceTemplateMode}d. This will be auto apply to future workspaces.`,
        {
          transition: Slide,
        }
      );
      if (workspaceTemplateMode === 'update') {
        trackWorkspaceTemplate({
          action: 'update template',
          settings: linePlotSettings,
        });
      }
      closeWorkspaceTemplateDrawer();
      refetchWorkspaceTemplates();
    } catch {
      setError(
        `Unable to ${workspaceTemplateMode} template. Please try again.`
      );
    }
  }, [
    entityOrgId,
    setError,
    upsertWorkspaceTemplate,
    linePlotSettings,
    viewerWorkspaceTemplate?.id,
    workspaceTemplateMode,
    closeWorkspaceTemplateDrawer,
    refetchWorkspaceTemplates,
  ]);

  const isLoading = isLoadingWorkspaceTemplates || isUpsertingWorkspaceTemplate;

  const isButtonDisabled = useMemo(() => {
    // If the existing template already has the same setting as the workspace
    // level setting, then there's no need to update the template
    const isSame = LINE_PLOT_TEMPLATE_FIELDS.every(
      key => linePlot[key] === viewerWorkspaceTemplate?.linePlotSettings?.[key]
    );
    return isSame || isLoading;
  }, [isLoading, linePlot, viewerWorkspaceTemplate?.linePlotSettings]);

  return (
    <div
      className={twMerge(
        TW_DRAWER_WIDTH,
        'flex h-[80px] ',
        'fixed bottom-0',
        'px-24 py-20',
        'border-t-[1px] border-solid border-moon-250',
        '[&>*]:grow'
      )}>
      <Button
        aria-label={`${capitalizeFirst(workspaceTemplateMode)} template`}
        variant="primary"
        className="grow"
        onClick={onButtonClick}
        disabled={isButtonDisabled}>
        {isLoading ? (
          <InstrumentedCenteredWaveLoader
            name="workspace-templates-save"
            size="small"
          />
        ) : (
          capitalizeFirst(workspaceTemplateMode)
        )}
      </Button>
    </div>
  );
};

/**
 * This function returns the values that should be used to update the workspace template. Make sure we are using the values and not the display values
 */
const useWorkspaceLinePlotValues = ({
  workspaceTemplate,
  currWorkspaceLinePlotSettings,
}: {
  workspaceTemplate: WorkspaceTemplate;
  currWorkspaceLinePlotSettings: LinePlotSettings;
}): LinePlotSettings => {
  const templateSections = useTemplateSections({
    workspaceTemplate,
    currWorkspaceLinePlotSettings,
  });

  return useMemo(
    () =>
      templateSections.reduce<LinePlotTemplateSettings>((acc, section) => {
        return section.rows.reduce((acc, row) => {
          // This is safe to do since we've constrainted the types from the query already
          // Make sure we preserve any undefined values
          acc[row.field] = row.newValue as any;
          return acc;
        }, acc);
      }, {} as LinePlotTemplateSettings),
    [templateSections]
  );
};
